/**
 * myDraft CMS — Context-Dependent Module Bootstrap
 *
 * Detects which modules a page needs based on DOM selectors and loads
 * only those modules. Falls back to legacy jquery_user_main.js if
 * no TypeScript bundles are built.
 *
 * @package MyDraft\Frontend
 * @version 1.1.0
 */

import type { IMyDraftModuleOptions, IMyDraftModule } from './types/module';

// ---------------------------------------------------------------------------
// Module manifest — maps module keys to their named exports.
// Each module MUST export `const XxxModule: IMyDraftModule`.
// ---------------------------------------------------------------------------
const moduleLoader = {
  async search(): Promise<IMyDraftModule> {
    const m = await import('../modules/search');
    return m.SearchModule;
  },
  async cart(): Promise<IMyDraftModule> {
    const m = await import('../modules/cart');
    return m.CartModule;
  },
  async kontakt(): Promise<IMyDraftModule> {
    const m = await import('../modules/kontakt');
    return m.KontaktModule;
  },
  async lazyLoader(): Promise<IMyDraftModule> {
    const m = await import('../modules/lazy-loader');
    return m.LazyLoaderModule;
  },
  async mobileBreakpoint(): Promise<IMyDraftModule> {
    const m = await import('../modules/mobile-breakpoint');
    return m.MobileBreakpointModule;
  },
  async newsletter(): Promise<IMyDraftModule> {
    const m = await import('../modules/newsletter');
    return m.NewsletterModule;
  },
  async portal(): Promise<IMyDraftModule> {
    const m = await import('../modules/portal');
    return m.PortalModule;
  },
} as const;

type ModuleKey = keyof typeof moduleLoader;

/**
 * DOM-based context detection: which modules does this page need?
 * Parses the DOM once to determine the required module set.
 */
function detectRequiredModules(): ModuleKey[] {
  const modules: ModuleKey[] = [];

  // Always load mobile breakpoint system
  modules.push('mobileBreakpoint');

  // Lazy-loaded CMS modules (via data-module-ajax)
  if (document.querySelector('[data-module-ajax]')) {
    modules.push('lazyLoader');
  }

  // Search forms
  if (document.querySelector('#txtQuickSearch')) {
    modules.push('search');
  }

  // Shopping cart
  if (
    document.querySelector('.shop_cat_list, #cart_item_add, #shop_footer, form[id^="cart_item_add_"]')
  ) {
    modules.push('cart');
  }

  // Contact form
  if (document.querySelector('form[id^="setKontaktForm"], .kontakt-form-widget')) {
    modules.push('kontakt');
  }

  // Newsletter
  if (document.querySelector('.tsec-newsletter-module-wrapper, form[id*="newsletter"]')) {
    modules.push('newsletter');
  }

  // Registration & Portal login (both handled by portal.ts)
  if (
    document.querySelector(
      'form[id*="registrieren"], form[id*="registration"], form[id*="signup"], #portal_login, form[id*="portal_login"]'
    )
  ) {
    modules.push('portal');
  }

  return [...new Set(modules)];
}

/**
 * Extracts CMS context from the DOM for all modules.
 */
function getModuleOptions(): IMyDraftModuleOptions {
  return {
    pageId: parseInt(document.body.getAttribute('data-page-id') || '0', 10),
    language: document.documentElement.lang || 'de',
    domainId: parseInt(document.body.getAttribute('data-domain-id') || '1', 10),
    baseUrl: location.origin,
  };
}

/**
 * Main entry point. Detects, loads, and initializes required modules.
 */
export async function bootstrapModules(): Promise<void> {
  const neededModules = detectRequiredModules();
  const options = getModuleOptions();

  console.debug(
    `[myDraft] Bootstrapping modules: ${neededModules.join(', ')} (page: ${options.pageId}, lang: ${options.language})`
  );

  // Load all needed modules in parallel
  const results = await Promise.allSettled(
    neededModules.map(async (key) => {
      try {
        const module = await moduleLoader[key]();
        module.init(options);
        console.debug(`[myDraft] ✓ Module loaded: ${key}`);
      } catch (err) {
        console.warn(`[myDraft] ✗ Module failed to load: ${key}`, err);
      }
    })
  );

  const loaded = results.filter((r) => r.status === 'fulfilled').length;
  const failed = results.filter((r) => r.status === 'rejected').length;
  console.debug(`[myDraft] Bootstrap complete: ${loaded} loaded, ${failed} failed`);
}

// Auto-bootstrap on DOM ready (if not already initialized)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => bootstrapModules());
} else {
  bootstrapModules();
}